import { useCallback, useEffect, useState, type RefObject } from 'react'
import { useAppStore } from '@/store/app'
import { compareIds, useSocialStore } from '@/store/social'

/**
 * Переход к сообщению (поиск, закреп, ответ, ссылка): если оно уже на экране — прокручиваем
 * к нему и подсвечиваем, иначе грузим окно вокруг и делаем то же, когда оно придёт.
 *
 * Хук надо вызывать раньше эффекта первой загрузки канала: окно вокруг сообщения тогда
 * начинает грузиться первым, и loadMessages не перетирает его последними сообщениями.
 *
 * latestId — последнее сообщение канала из стора: по нему видно, что окно оторвано от конца
 * и нужна кнопка «Перейти к новым».
 */
export function useMessageJump(channelId: string, scrollerRef: RefObject<HTMLDivElement | null>, stickRef: RefObject<boolean>, latestId: () => string | null | undefined) {
  const jumpTarget = useAppStore((s) => s.jumpTarget)
  const setJumpTarget = useAppStore((s) => s.setJumpTarget)
  const messages = useSocialStore((s) => s.messages[channelId])
  const loadAround = useSocialStore((s) => s.loadAround)
  const loadMessages = useSocialStore((s) => s.loadMessages)
  // какое сообщение ждём и какое подсвечено; оба меняются прямо при отрисовке, как советует React
  // для состояния, выводимого из пропсов и стора, — эффекты только грузят и прокручивают
  const [seen, setSeen] = useState<typeof jumpTarget>(null)
  const [pending, setPending] = useState<string | null>(null)
  const [highlight, setHighlight] = useState<string | null>(null)
  // на экране окно из середины истории: новые сообщения ниже не догружаются сами
  const [detached, setDetached] = useState(false)

  if (jumpTarget !== seen) {
    setSeen(jumpTarget)
    if (jumpTarget?.channelId === channelId) setPending(jumpTarget.messageId)
  }
  if (pending && messages?.some((m) => m.id === pending)) {
    setHighlight(pending)
    setPending(null)
  }

  useEffect(() => {
    if (!jumpTarget || jumpTarget.channelId !== channelId) return
    const { messageId } = jumpTarget
    setJumpTarget(null)
    stickRef.current = false
    if (useSocialStore.getState().messages[channelId]?.some((m) => m.id === messageId)) return
    loadAround(channelId, messageId)
      .then(() => {
        const list = useSocialStore.getState().messages[channelId] ?? []
        // сообщение удалили — остаёмся у соседних, подсвечивать нечего
        if (!list.some((m) => m.id === messageId)) setPending(null)
        const newest = [...list].reverse().find((m) => !m.pending)?.id
        const latest = latestId()
        setDetached(Boolean(newest && latest && compareIds(latest, newest) > 0))
      })
      .catch(() => setPending(null))
  }, [jumpTarget, channelId, loadAround, setJumpTarget, stickRef, latestId])

  useEffect(() => {
    if (!highlight) return
    scrollerRef.current?.querySelector(`[data-message-id="${highlight}"]`)?.scrollIntoView({ block: 'center' })
    const timer = setTimeout(() => setHighlight(null), 2500)
    return () => clearTimeout(timer)
  }, [highlight, scrollerRef])

  /** Вернуться к последним сообщениям: окно из середины заменяется концом истории. */
  const toPresent = useCallback(async (reload = true) => {
    if (reload) {
      if (useSocialStore.getState().loading[channelId]) throw new Error('История ещё загружается. Попробуйте снова')
      await loadMessages(channelId)
    }
    setDetached(false)
    setHighlight(null)
    setPending(null)
    stickRef.current = true
  }, [channelId, loadMessages, stickRef])

  return { highlight, detached, toPresent, jumping: Boolean(pending || highlight) }
}
